import * as THREE from 'three';
import { ISceneInfo, EEntityType, IWallEdge } from '../types';
import { COMPRESS_SIZE } from '../constant/capture';
import { globalStores } from '../stores';
import { modelManager } from '../manager/entityManager';
import { pixelRatio2d } from '../math/const';
import { ArcSeg2d } from '../math/arcSeg2d';
import {
  ENTITY_RENDER_ORDER,
  column2dColor,
  defaultOutlineColor,
  doorBgOutlineColor,
  separateWallBGColor,
  separateWallOutline,
  virtualSeparateWallLineColor,
  wall2dColor,
  windowBgColor
} from '../constant/entity';
import { sceneBgColor } from '../constant/color';
import { EFaceType } from '../entities/type';
import { createMaterial } from '../data/material/utils';
import { EColumnType } from '../data/model/model';
import { ESeparateWallType, SeparateWallData } from '../data/separateWall/SeparateWall';
import { calAuxiliarySegs } from '../data/separateWall/utils';
import { worldPosToRenderPos } from '.';
import { getColumnBaseOutline } from './editStructUtils';
import { dispersePath } from './shape';
import { sampleShapePath } from './wallShape';
import { CaptureProfiler } from './capturePerf';
import {
  addBatchedLines,
  addDoorWindow,
  createFitOrthographicCamera,
  createFloorGeometry,
  createFurnitureMesh,
  createLineBatches,
  createShapeGeometry,
  createWallGeometry,
  getLineBatch,
  pushClosedLoop,
  pushPolyline,
  pushSegment,
  resolveFitView,
  type IDoorWindowMaterials,
  type TLineBatches
} from './draw2d';

/**
 * 方案缩略图截图：从 store 数据离屏重建一个只含构件的场景，而不是截画布。
 *
 * 由此得到两个性质：
 * 1. 编辑器新增任何 model（交互辅助、指示器、临时高亮等）都不需要为截图做任何处理，默认不进图；
 * 2. 选中/hover/入户门指示这类临时态在数据里不存在，绘制时也不读，结构上不可能被截进去。
 *
 * 代价是新增「需要出现在缩略图里」的构件时，要来 composeCaptureScene 加一行。
 */

/**
 * 渲染分辨率 = 输出分辨率 × 该倍数。抗锯齿由 RenderTarget 的 MSAA 提供，所以默认不超采样。
 * 实测纯色边界上两者等价，而超采样会让所有 1 设备像素宽的轮廓线在降采样后减半变淡，
 * 像素数还按平方增长（读回像素、翻转、编码都随之线性增长）。
 */
const RENDER_SUPERSAMPLE = 1;
/** RenderTarget 的多重采样数。resolve 发生在 render() 结束时，之后读回的就是抗锯齿后的像素 */
const RENDER_MSAA_SAMPLES = 4;
/** 取景留白比例 */
const SCENE_PADDING = 0.1;
/** 圆柱的圆周分段数，与 ColumnModel 一致 */
const CIRCLE_COLUMN_SEGMENTS = 64;

/**
 * 截图专用材质，颜色取自 constant/entity 里的常态色。
 * 不复用画布上的共享 LineMaterial 实例：Line2 的线宽依赖 material.resolution，
 * 而截图的渲染尺寸和画布不同，改共享实例会污染画布显示。
 */
const createCaptureMaterials = () => ({
  wall: new THREE.MeshBasicMaterial({ color: wall2dColor }),
  wallOutline: new THREE.LineBasicMaterial({ color: defaultOutlineColor }),
  column: new THREE.MeshBasicMaterial({ color: column2dColor }),
  separateWallBg: new THREE.MeshBasicMaterial({ color: separateWallBGColor }),
  // 对应 SEPARATE_WALL_MATERIAL.outline[Default]
  separateWallOutline: new THREE.LineBasicMaterial({ color: separateWallOutline }),
  // 对应 SEPARATE_WALL_MATERIAL.dashLine[Default]，虚拟墙的中心线用的是另一个颜色
  separateWallVirtualLine: new THREE.LineBasicMaterial({ color: virtualSeparateWallLineColor }),
  doorWindowBg: new THREE.MeshBasicMaterial({ color: windowBgColor, side: THREE.DoubleSide }),
  doorWindowOther: new THREE.MeshBasicMaterial({
    color: windowBgColor,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.6
  }),
  doorWindowOutline: new THREE.LineBasicMaterial({ color: defaultOutlineColor }),
  doorWindowBgOutline: new THREE.LineBasicMaterial({ color: doorBgOutlineColor }),
  doorWindowDashOutline: new THREE.LineDashedMaterial({
    color: defaultOutlineColor,
    dashSize: 0.05,
    gapSize: 0.025
  })
});

type TCaptureMaterials = ReturnType<typeof createCaptureMaterials>;

const getHiddenIds = (type: EEntityType) => globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(type);

/**
 * 取景依据：只按墙体算包围盒，墙外的家具不参与，避免画面被推歪。
 * 弧形墙的外扩要靠 dispersePath 把弧线打散成点，否则弦的两端算不出弧顶。
 * 没有墙体时返回空盒，由调用方按取景失败处理。
 */
const getWallBounds = (): THREE.Box3 => {
  const bounds = new THREE.Box3();

  for (const wall of globalStores.wallStore.getWallDatas()) {
    let wallShapePoints = wall.drawWallInfo.drawShape.flat();

    if (wall.baseData?.center) {
      wallShapePoints = wallShapePoints.concat(dispersePath(wallShapePoints));
    }

    for (const point of wallShapePoints) {
      const realPoint = worldPosToRenderPos(point);
      bounds.expandByPoint(new THREE.Vector3(realPoint.x, realPoint.y, 0));
    }
  }

  return bounds;
};

// ---------------------------------------------------------------------------
// 地板
// ---------------------------------------------------------------------------

const addFloorMesh = (
  scene: THREE.Scene,
  corePathInfos: IWallEdge[],
  coreHoleInfos: IWallEdge[][],
  materialId: string,
  z: number
) => {
  const geometry = createFloorGeometry(corePathInfos, coreHoleInfos);
  if (!geometry) return;

  // 地板必须走 createMaterial 才能带上用户选的材质（含贴图），它内部按 materialId 缓存，勿销毁
  const mesh = new THREE.Mesh(geometry, createMaterial(materialId, true));
  mesh.position.z = z;
  mesh.userData.sharedMaterial = true;
  scene.add(mesh);
};

const addFloors = (scene: THREE.Scene) => {
  const defaultFloorMaterialId = globalStores.materialStore.getDefaultMaterial(EFaceType.FloorFace)?.id ?? '';

  for (const roomData of globalStores.roomStore.getRoomDatas()) {
    const regionDatas = globalStores.regionStore.getRoomRegions(roomData.id);
    // 与 RoomManager._parseRoomData 一致：房间被划分成多个分区时，
    // 房间自身用默认地面材质，各分区再画自己的地面盖在上面
    const withRegion = regionDatas.length > 1;

    addFloorMesh(
      scene,
      roomData.corePathInfos,
      roomData.coreHoleInfos,
      withRegion ? defaultFloorMaterialId : roomData.materialInfo.id,
      ENTITY_RENDER_ORDER.floor
    );

    if (!withRegion) continue;

    for (const regionData of regionDatas) {
      addFloorMesh(
        scene,
        regionData.corePathInfos,
        regionData.coreHoleInfos,
        regionData.materialInfo.id,
        ENTITY_RENDER_ORDER.region
      );
    }
  }
};

// ---------------------------------------------------------------------------
// 墙体（含墙轮廓）
// ---------------------------------------------------------------------------

const addWallOutline = (batches: TLineBatches, materials: TCaptureMaterials, hiddenWallIds: Set<string>) => {
  // 与 WallOutlineManager 一致：取连通轮廓的所有边，隐藏的墙不参与
  const edges = globalStores.connectedWallOutlines
    .flatMap((outline) => outline.edges)
    .filter((edge) => !hiddenWallIds.has(edge.wallId));

  const positions = getLineBatch(batches, materials.wallOutline);
  const z = ENTITY_RENDER_ORDER.wallOutline;

  for (const edge of edges) {
    if (!edge.center) {
      pushSegment(positions, edge.start, edge.end, z);
      continue;
    }

    const arc = new ArcSeg2d({
      start: edge.start,
      end: edge.end,
      center: edge.center,
      clockwise: edge.clockwise ?? false
    });
    pushPolyline(positions, arc.getPoints(), z);
  }
};

const addWalls = (scene: THREE.Scene, materials: TCaptureMaterials, batches: TLineBatches) => {
  const hiddenWallIds = getHiddenIds(EEntityType.Wall);
  const wallDatas = globalStores.wallStore.getWallDatas().filter((data) => !hiddenWallIds.has(data.id));

  for (const wallData of wallDatas) {
    // 与 WallEntity 一致：只取 drawShape[0]
    const outline = wallData.drawWallInfo.drawShape[0];
    if (!outline?.length) continue;

    const geometry = createWallGeometry([outline]);
    if (!geometry) continue;

    const mesh = new THREE.Mesh(geometry, materials.wall);
    mesh.position.z = ENTITY_RENDER_ORDER.wall;
    scene.add(mesh);
  }

  addWallOutline(batches, materials, hiddenWallIds);
};

// ---------------------------------------------------------------------------
// 隔墙
// ---------------------------------------------------------------------------

const addSeparateWallCenterLine = (
  batches: TLineBatches,
  materials: TCaptureMaterials,
  separateWallData: SeparateWallData
) => {
  const positions = getLineBatch(batches, materials.separateWallVirtualLine);
  const z = ENTITY_RENDER_ORDER.separateWall;

  if (separateWallData.center) {
    pushPolyline(
      positions,
      new ArcSeg2d({
        start: separateWallData.start,
        end: separateWallData.end,
        center: separateWallData.center,
        clockwise: separateWallData.clockwise ?? false
      }).getPoints(),
      z
    );
    return;
  }

  pushSegment(positions, separateWallData.start, separateWallData.end, z);
};

const addSeparateWalls = (scene: THREE.Scene, materials: TCaptureMaterials, batches: TLineBatches) => {
  const hiddenIds = getHiddenIds(EEntityType.SeparateWall);
  const separateWallDatas = globalStores.separateWallStore.separateWallDatas.filter((data) => !hiddenIds.has(data.id));

  for (const separateWallData of separateWallDatas) {
    // 与 SeparateWallModel 一致：虚拟墙常态下背景材质 opacity 为 0，只有中心线可见
    // （live 那份材质名叫 dashLine，但并没有开 dashed，实际是实线）
    if (separateWallData.type === ESeparateWallType.VirtualWall) {
      addSeparateWallCenterLine(batches, materials, separateWallData);
      continue;
    }

    const drawShape = sampleShapePath(separateWallData.drawSeparateWallInfo.drawShape);
    if (!drawShape.length) continue;

    const bgMesh = new THREE.Mesh(createShapeGeometry(drawShape), materials.separateWallBg);
    bgMesh.position.z = ENTITY_RENDER_ORDER.separateWall;
    scene.add(bgMesh);

    // 轮廓 = 墙体路径 + 分隔辅助线，与 SeparateWallModel.createOutlineModel 一致
    const positions = getLineBatch(batches, materials.separateWallOutline);
    const outlineZ = ENTITY_RENDER_ORDER.separateWallOutline;
    pushClosedLoop(positions, drawShape, outlineZ);

    for (const seg of calAuxiliarySegs(separateWallData)) {
      if (seg instanceof ArcSeg2d) {
        pushPolyline(positions, seg.getPoints(), outlineZ);
      } else {
        pushSegment(positions, seg.start, seg.end, outlineZ);
      }
    }
  }
};

// ---------------------------------------------------------------------------
// 柱子
// ---------------------------------------------------------------------------

const addColumns = (scene: THREE.Scene, materials: TCaptureMaterials) => {
  const hiddenIds = getHiddenIds(EEntityType.Column);
  const columnDatas = globalStores.structStore.getColumnDatas().filter((data) => !hiddenIds.has(data.id));

  for (const columnData of columnDatas) {
    let geometry: THREE.BufferGeometry;

    if (columnData.type === EColumnType.squareColumn) {
      geometry = createShapeGeometry(getColumnBaseOutline(columnData, { x: 0, y: 0 }));
    } else {
      if (!columnData.radius) continue;
      geometry = new THREE.CircleGeometry(columnData.radius / pixelRatio2d, CIRCLE_COLUMN_SEGMENTS);
    }

    // 常态下柱子只有填充色：ColumnModel.updateStatus 在 normal/virtual 态把轮廓线隐藏了，
    // 轮廓只在 hover/选中时出现，属于临时态，不画
    const mesh = new THREE.Mesh(geometry, materials.column);
    mesh.position.set(
      columnData.center.x / pixelRatio2d,
      columnData.center.y / pixelRatio2d,
      ENTITY_RENDER_ORDER.column
    );
    mesh.rotation.z = columnData.rotation;
    scene.add(mesh);
  }
};

// ---------------------------------------------------------------------------
// 门窗
// ---------------------------------------------------------------------------

/** 编辑器的门窗常态取色，映射到共享绘制层要的那组材质 */
const toDoorWindowMaterials = (materials: TCaptureMaterials): IDoorWindowMaterials => ({
  bg: materials.doorWindowBg,
  other: materials.doorWindowOther,
  outline: materials.doorWindowOutline,
  bgOutline: materials.doorWindowBgOutline,
  dashOutline: materials.doorWindowDashOutline
});

const addDoorWindows = (scene: THREE.Scene, materials: TCaptureMaterials, batches: TLineBatches) => {
  const hiddenIds = getHiddenIds(EEntityType.DoorWindow);
  const windowDatas = globalStores.modelStore.getWindowDatas().filter((data) => !hiddenIds.has(data.id));
  const doorWindowMaterials = toDoorWindowMaterials(materials);

  for (const windowData of windowDatas) {
    addDoorWindow(scene, batches, windowData, doorWindowMaterials);
  }
};

// ---------------------------------------------------------------------------
// 家具
// ---------------------------------------------------------------------------

const addFurniture = (scene: THREE.Scene) => {
  const hiddenIds = getHiddenIds(EEntityType.Furniture);
  const furnitureDatas = globalStores.modelStore.getFurnitureDatas().filter((data) => !hiddenIds.has(data.id));
  if (!furnitureDatas.length) return;

  const instanceManager = modelManager.furnitureManager.instanceManager;
  const group = new THREE.Group();
  // InstancedMesh 池整体带着这个 z，实例矩阵是相对它的
  group.position.z = ENTITY_RENDER_ORDER.furniture;

  for (const furnitureData of furnitureDatas) {
    const matrix = instanceManager.getInstanceMatrix(furnitureData.id);
    const svgContent = globalStores.modelStore.getModel(furnitureData.modelId)?.model2d;
    // 没有注册实例（缺 2d 模型）的家具在画布上也只有一个近乎全透明的占位符，不进图
    if (!matrix || !svgContent) continue;

    // 用画布上现成的实例矩阵，位置/缩放/翻转/叠放顺序都不必重算
    const mesh = createFurnitureMesh(svgContent, matrix);
    if (mesh) group.add(mesh);
  }

  scene.add(group);
};

// ---------------------------------------------------------------------------
// 场景组装与渲染
// ---------------------------------------------------------------------------

/**
 * 截图内容的唯一约定：只有这里列出的构件会出现在缩略图里。
 * 新增构件要进图就在这里加一行；不加就默认不进图。
 */
const composeCaptureScene = (materials: TCaptureMaterials) => {
  const scene = new THREE.Scene();
  // 各构件的轮廓线先按材质攒着，最后一次性合并成几个 LineSegments
  const batches = createLineBatches();

  addFloors(scene);
  addWalls(scene, materials, batches);
  addSeparateWalls(scene, materials, batches);
  addColumns(scene, materials);
  addDoorWindows(scene, materials, batches);
  addFurniture(scene);

  addBatchedLines(scene, batches);

  return scene;
};

const disposeCaptureScene = (scene: THREE.Scene, materials: TCaptureMaterials) => {
  scene.traverse((object) => {
    const geometry = (object as THREE.Mesh | THREE.LineSegments).geometry;
    if (geometry && !object.userData.sharedGeometry) geometry.dispose();
  });
  scene.clear();

  // 只销毁本次截图自己造的材质；地板和家具用的是全局缓存材质
  Object.values(materials).forEach((material) => material.dispose());
};

/** 按取景宽高比确定输出与渲染尺寸：长边固定，不再受画布尺寸影响 */
const resolveCaptureSize = (aspect: number) => {
  const outputWidth = aspect >= 1 ? COMPRESS_SIZE : Math.max(1, Math.round(COMPRESS_SIZE * aspect));
  const outputHeight = aspect >= 1 ? Math.max(1, Math.round(COMPRESS_SIZE / aspect)) : COMPRESS_SIZE;

  return {
    outputWidth,
    outputHeight,
    renderWidth: outputWidth * RENDER_SUPERSAMPLE,
    renderHeight: outputHeight * RENDER_SUPERSAMPLE
  };
};

/** WebGL 读回的像素是自下而上的，原地按行对调，省掉一块只为翻转而存在的画布 */
const flipRowsInPlace = (pixelBuffer: Uint8ClampedArray, width: number, height: number) => {
  const rowBytes = width * 4;
  const rowCache = new Uint8ClampedArray(rowBytes);

  for (let top = 0, bottom = height - 1; top < bottom; top++, bottom--) {
    const topStart = top * rowBytes;
    const bottomStart = bottom * rowBytes;
    rowCache.set(pixelBuffer.subarray(topStart, topStart + rowBytes));
    pixelBuffer.copyWithin(topStart, bottomStart, bottomStart + rowBytes);
    pixelBuffer.set(rowCache, bottomStart);
  }
};

const toOutputCanvas = (
  pixelBuffer: Uint8ClampedArray<ArrayBuffer>,
  renderWidth: number,
  renderHeight: number,
  outputWidth: number,
  outputHeight: number
): HTMLCanvasElement | null => {
  flipRowsInPlace(pixelBuffer, renderWidth, renderHeight);

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return null;

  canvas.width = renderWidth;
  canvas.height = renderHeight;
  // 直接把读回的 buffer 交给 ImageData，不再额外分配和拷贝一份全尺寸像素
  context.putImageData(new ImageData(pixelBuffer, renderWidth, renderHeight), 0, 0);

  if (renderWidth === outputWidth && renderHeight === outputHeight) return canvas;

  // 只有开了超采样才需要降采样，这时才多用一块输出画布
  const outputCanvas = document.createElement('canvas');
  const outputContext = outputCanvas.getContext('2d');
  if (!outputContext) return null;

  outputCanvas.width = outputWidth;
  outputCanvas.height = outputHeight;
  outputContext.imageSmoothingEnabled = true;
  outputContext.imageSmoothingQuality = 'high';
  outputContext.drawImage(canvas, 0, 0, renderWidth, renderHeight, 0, 0, outputWidth, outputHeight);

  return outputCanvas;
};

/**
 * 用 toBlob 而不是 toDataURL：上传要的本来就是二进制，base64 白多一次编码（体积 +33%）和上传前的一次解码；
 * 且 toBlob 是异步的，图像编码不占着前面那个同步块。
 *
 * 编码成有损格式而不是 PNG：带贴图的地板是照片类内容，PNG 无损压不动，一张 1536 的图能到 1MB 以上。
 * 眼下先出 JPEG 看体积，WebP 整条路原样留着，改这一个常量就切回去。
 * 浏览器不支持 WebP 编码时 toBlob 会按规范回落成 PNG，blob.type 会跟着变，
 * 所以上传的 Content-Type 必须读 blob.type，不能写死。
 */
const CAPTURE_IMAGE_TYPE: 'image/webp' | 'image/jpeg' = 'image/jpeg';
const CAPTURE_IMAGE_QUALITY = 0.75;

/**
 * JPEG 没有 alpha 通道，透明像素在编码时会按规范合成到黑底上，所以得自己铺一层不透明底色；
 * WebP 带 alpha，仍旧出透明底。底色用 sceneBgColor（#f7f9fa），与方案表格/卡片/历史版本里
 * 预览图那一格的 --color-background-light 同色，图铺进去看不出边界。
 */
const CAPTURE_OPAQUE_BACKGROUND = CAPTURE_IMAGE_TYPE === 'image/jpeg';

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, CAPTURE_IMAGE_TYPE, CAPTURE_IMAGE_QUALITY));

/**
 * 同步部分：组装场景、渲染、读回像素，产出一张画布。
 * 这一段会临时切换画布 renderer 的 renderTarget 和 clearColor，必须一口气做完，不能被打断。
 */
const renderCaptureCanvas = (renderer: THREE.WebGLRenderer, profiler: CaptureProfiler): HTMLCanvasElement | null => {
  const bounds = getWallBounds();
  if (bounds.isEmpty()) return null;

  const boundsSize = bounds.getSize(new THREE.Vector3());
  const { outputWidth, outputHeight, renderWidth, renderHeight } = resolveCaptureSize(
    (boundsSize.x * (1 + SCENE_PADDING * 2)) / (boundsSize.y * (1 + SCENE_PADDING * 2))
  );
  const fitView = resolveFitView(bounds, outputWidth / outputHeight, SCENE_PADDING);
  if (!fitView) return null;
  profiler.meta('渲染尺寸', `${renderWidth}×${renderHeight}`);
  profiler.meta('输出尺寸', `${outputWidth}×${outputHeight}`);
  profiler.step('取景');

  const materials = createCaptureMaterials();
  const scene = composeCaptureScene(materials);
  profiler.step('组装场景');

  const camera = createFitOrthographicCamera(fitView);

  const renderTarget = new THREE.WebGLRenderTarget(renderWidth, renderHeight, { samples: RENDER_MSAA_SAMPLES });
  renderTarget.texture.colorSpace = THREE.SRGBColorSpace;
  const originalRenderTarget = renderer.getRenderTarget();

  renderer.setRenderTarget(renderTarget);
  renderer.setClearColor(sceneBgColor, CAPTURE_OPAQUE_BACKGROUND ? 1 : 0);
  renderer.render(scene, camera);
  renderer.setRenderTarget(originalRenderTarget);
  renderer.setClearColor(sceneBgColor, 1);
  profiler.step('渲染');

  // 用 Uint8ClampedArray 而非 Uint8Array：readPixels 两者都接受，而 ImageData 只收前者，省一次包装
  const pixelBuffer = new Uint8ClampedArray(renderWidth * renderHeight * 4);
  renderer.readRenderTargetPixels(renderTarget, 0, 0, renderWidth, renderHeight, pixelBuffer);
  profiler.step('读回像素');

  const canvas = toOutputCanvas(pixelBuffer, renderWidth, renderHeight, outputWidth, outputHeight);
  profiler.step('翻转');

  renderTarget.dispose();
  disposeCaptureScene(scene, materials);
  profiler.step('销毁');

  return canvas;
};

export const capture2d = async (sceneInfo: Pick<ISceneInfo, 'renderer'>): Promise<Blob | null> => {
  const profiler = new CaptureProfiler('captureScene', sceneInfo.renderer);

  const canvas = renderCaptureCanvas(sceneInfo.renderer, profiler);
  if (!canvas) return null;

  const blob = await canvasToBlob(canvas);
  profiler.step('编码');
  profiler.end(blob);

  return blob;
};

export const capture2dAsync = (sceneInfo: Pick<ISceneInfo, 'renderer'>): Promise<Blob | null> => {
  return new Promise((resolve) => {
    if (!document.hidden && 'requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          resolve(capture2d(sceneInfo));
        },
        { timeout: 1000 }
      );
    } else {
      const timer = window.self.setTimeout(() => {
        window.self.clearTimeout(timer);
        resolve(capture2d(sceneInfo));
      }, 100);
    }
  });
};
